import * as Sentry from '@sentry/react';

import { gengql } from '@/__generated__';
import {
  AgreementInput,
  AuthCreateTokenMutation,
  SignupSubtype,
  SignupType,
} from '@/__generated__/graphql';
import { notify } from '@/actions/ui';
import { authSession } from '@/auth/utils/authSession';
import { AUTH_TOKEN_CACHE_KEY } from '@/auth/utils/authTokenKey';
import config from '@/config';
import { gtm } from '@/core/analytics';
import { ERROR_CODES, GraphQLClient, basicAuth, hasErrorCode } from '@/core/api';
import { clearInvitationToken, getInvitationToken } from '@/core/invite';
import { clearTracking } from '@/core/tracking';
import { UserContext } from '@/core/user';
import { Viewer } from '@/core/viewer';
import { GroupDomain } from '@/group';
import { useApp } from '@/hooks/useAppContext';
import store, { AppStore } from '@/store';
import { setRuntimeVariable } from '@/store/runtime';
import { clearCache, setCache } from '@/utils';

export { SignupSubtype, SignupType } from '@/__generated__/graphql';
export const defaultNext = '/dashboard';

function formatPath(path: string, domain?: GroupDomain, signupType?: SignupType) {
  const domainPart = domain ? `/${domain.subdomain}` : '';
  const signupTypePart = signupType ? `/${signupType === 'client' ? 'member' : signupType}` : '';
  return `${domainPart}${path}${signupTypePart}`;
}

function addSearchParam(urlStr: string, field: string, value?: unknown) {
  if (!value) return urlStr;
  const querySign = urlStr.indexOf('?') === -1 ? '?' : '&';
  return `${urlStr}${querySign}${field}=${value}`;
}

interface AuthPathOptions {
  to: string;
  domain?: GroupDomain;
  signupType?: SignupType;
  invite?: string;
  next?: string;
}

export function formatAuthPath({ to, domain, signupType, invite, next }: AuthPathOptions): string {
  let path = to;
  path = formatPath(path, domain, signupType);
  path = addSearchParam(path, 'invite', invite);
  path = addSearchParam(path, 'next', encodeURIComponent(next || ''));
  return path;
}

// At least 8 characters long
// At least 1 uppercase letter, 1 lowercase letter, 1 digit and 1 special char
// List of special chart https://owasp.org/www-community/password-special-characters
const regex =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\s"'^`~!#%&,-/:;<=>@_[\]\\$.|?*+(){}])[A-Za-z\d\s"'^`~!#%&,-/:;<=>@_[\]\\$.|?*+(){}]{8,16}/;
export function isValidPassword(password: string): boolean {
  if (!password) return false;
  return regex.test(password);
}

export type TokenMeta = AuthCreateTokenMutation['createToken'];

interface LoginMeta {
  inviteToken?: string;
  [key: string]: unknown;
}

async function createToken(
  client: GraphQLClient,
  userId: string | undefined,
  loginMeta: LoginMeta,
  recaptchaToken?: string
): Promise<TokenMeta> {
  const { inviteToken } = loginMeta || {};
  const data = await client.mutate(
    gengql(/* GraphQL */ `
      mutation authCreateToken(
        $userId: String
        $inviteToken: String
        $loginMeta: Json
        $recaptchaToken: String
      ) {
        createToken(
          user_id: $userId
          invite_token: $inviteToken
          login_meta: $loginMeta
          recaptcha_token: $recaptchaToken
          client_id: "node"
        ) {
          user_id
          invite_token
          login_meta
          token
        }
      }
    `),
    { userId, inviteToken, loginMeta, recaptchaToken }
  );

  return data.createToken;
}

export function logout(): void {
  clearCache('session');
  // ENG-5251 (PR #2688 review): route through authSession so runtime.token
  // is cleared in Redux symmetrically with the cache wipe. Previously this
  // bypass left `runtime.token` stale, and the WebSocket token-getter at
  // index.tsx:252 kept seeing the pre-logout value until the next reload.
  authSession.clearToken();
  clearCache('user_context');
  // Note: rate display cookie intentionally NOT cleared - persists for rate display
}

export async function loginAs(
  client: GraphQLClient,
  store: AppStore,
  userIdOrEmail: string
): Promise<void> {
  const data = await client.query(
    gengql(/* GraphQL */ `
      query getUser($userId: String!) {
        user(id: $userId) {
          id
          username
        }
      }
    `),
    { userId: userIdOrEmail }
  );

  if (!data.user) {
    throw new Error('User not found');
  }

  const userId = data.user.id;
  try {
    const token = await createToken(client, userId, {
      method: 'admin',
      ua: window.navigator.userAgent,
    });
    logout();

    setCache(AUTH_TOKEN_CACHE_KEY, token);
    // ENG-5251 (PR #2688 review): mirror the cache write into runtime.token
    // so the WebSocket lazy token-getter (index.tsx:252) sees the new auth
    // without waiting for a page reload.
    store.dispatch(setRuntimeVariable('token', token.token));
  } catch {
    await store.dispatch(notify("You don't have permission to perform this action", 'error'));
    throw new Error('Login as user failed');
  }
}

// SignupSubtype distinguishes funnels within a SignupType. Today only
// SelfService is meaningful (ENG-5201): the API uses it to suppress the
// welcome+verify-link email because that flow uses email-OTP for verification.
// Omitting it preserves the legacy welcome-link behavior.
interface CreateUserOptions {
  email: string;
  password: string;
  first_name: string;
  last_name: string;
  recaptcha_token?: string;
  landing_url?: string;
  invite?: string;
  tags?: string[];
  signup_type?: SignupType;
  signup_subtype?: SignupSubtype;
  subdomain?: string;
  phone?: string;
  agreements?: AgreementInput[];
}

async function createUser(client: GraphQLClient, options: CreateUserOptions) {
  const res = await client.mutate(
    gengql(/* GraphQL */ `
      mutation createUser(
        $email: String!
        $password: String!
        $first_name: String!
        $last_name: String!
        $recaptcha_token: String
        $landing_url: String
        $invite: String
        $tags: [String!]
        $signup_type: SignupType
        $signup_subtype: SignupSubtype
        $subdomain: String
        $phone: String
        $agreements: [AgreementInput!]
      ) {
        createUser(
          email: $email
          password: $password
          first_name: $first_name
          last_name: $last_name
          recaptcha_token: $recaptcha_token
          landing_url: $landing_url
          invitation_token: $invite
          tags: $tags
          signup_type: $signup_type
          signup_subtype: $signup_subtype
          subdomain: $subdomain
          phone: $phone
          agreements: $agreements
        ) {
          user {
            id
          }
        }
      }
    `),
    options
  );

  return res.createUser;
}

// ENG-5760: createUser succeeded but the token step failed, so the account
// exists without a session. Callers should send the user to sign in; a retry
// can only fail with "email already exists". `message` is safe to show the
// user as-is, and `cause` keeps the createToken failure for Sentry.
export class SignupSessionError extends Error {
  declare readonly cause: unknown;

  constructor(cause: unknown) {
    super(
      "Your account was created, but we couldn't sign you in automatically. Please sign in to continue."
    );
    this.name = 'SignupSessionError';
    // Non-enumerable like a native ES2022 cause (the lib target is ES2020), so
    // console capture and JSON.stringify don't walk the APIError's variables.
    Object.defineProperty(this, 'cause', { value: cause, configurable: true, writable: true });
  }
}

// `next: string` redirects after successful signup (legacy behavior).
// `next: false` suppresses the redirect AND returns the auth token — used
// by ENG-5201's modal-resident self-service flow, which stays open to drive
// the email-OTP step. Callers in modal-mode are responsible for calling
// authSession.authenticate(token) so the in-page Apollo client picks up
// the new auth context (the redirect path doesn't need this because the
// page reload re-initializes Apollo from cache).
export async function signup(
  client: GraphQLClient,
  options: CreateUserOptions,
  next: string | false = defaultNext
): Promise<string | undefined> {
  const data = { ...options };
  data.invite = data.invite || getInvitationToken();
  //data.login = data.login || true;

  const resp = await createUser(client, data);

  gtm({
    event: 'signup',
    signupType: data.signup_type || 'unknown',
    userId: resp.user.id,
  });

  let token;
  try {
    // Built inside the try: the account already exists, so any failure from
    // here on is a SignupSessionError.
    // eslint-disable-next-line no-param-reassign
    client = new GraphQLClient(config.apiUrl, basicAuth(data.email, data.password));
    token = await createToken(client, undefined, {
      method: 'password',
      signup: true,
      signupType: data.signup_type || undefined,
      landingUrl: data.landing_url,
      ua: window.navigator.userAgent,
    });
  } catch (err) {
    // BasicAuthMiddleware answers an MFA challenge with a 401 carrying one of
    // these codes; its reason ("otp required") is not a stable contract.
    const mfaRequired =
      hasErrorCode(err, ERROR_CODES.AUTH_MFA_REQUIRED) ||
      hasErrorCode(err, ERROR_CODES.AUTH_MFA_SETUP_REQUIRED);
    if (!mfaRequired) {
      const sessionErr = new SignupSessionError(err);
      // Reported here so every signup form counts it the same way. This is the
      // stranded-signup loss metric: GTM's `signup` event has already fired.
      Sentry.captureException(sessionErr, {
        level: 'warning',
        tags: { signup_step: 'create_token' },
      });
      throw sessionErr;
    }
    // Legacy redirect path: stash on /dashboard and let the standard MFA
    // flow take over.
    if (next !== false) {
      window.location.assign(next);
      return undefined;
    }
    // Modal-resident callers (ENG-5201 self-service signup) need an
    // actionable code to render a guided "Sign in to finish MFA setup" CTA
    // — returning undefined leaves them with no way to recover since the
    // user account already exists server-side. Both MFA codes are translated
    // to the AUTH_MFA_ENROLLMENT_REQUIRED registry entry those callers key on.
    const mfaErr = new Error((err as Error).message);
    (mfaErr as Error & { code?: string }).code = ERROR_CODES.AUTH_MFA_ENROLLMENT_REQUIRED;
    throw mfaErr;
  }

  clearInvitationToken();
  clearTracking();

  setCache(AUTH_TOKEN_CACHE_KEY, token);
  // ENG-5251 (PR #2688 review): see logout() comment — keep runtime.token
  // in lockstep with the cache so the WebSocket token-getter at
  // index.tsx:252 sees the new auth. The redirect path triggers a reload
  // that would also re-populate runtime.token, but modal-resident callers
  // (next === false) stay in-page and depend on this dispatch.
  store.dispatch(setRuntimeVariable('token', token?.token));

  if (next !== false) {
    window.location.assign(next);
    return undefined;
  }
  // bearerAuth + authSession.authenticate expect the JWT string, not the
  // wrapping TokenMeta object stored in cache.
  return token?.token;
}

interface LoginOptions {
  email: string;
  password: string;
  otp?: string;
  recaptchaToken?: string;
  next?: string;
}

export default async function login({
  email,
  password,
  otp,
  recaptchaToken,
  next = defaultNext,
}: LoginOptions): Promise<void> {
  const inviteToken = getInvitationToken();

  const client = new GraphQLClient(config.apiUrl, basicAuth(email, password), {
    otp,
  });

  const token = await createToken(
    client,
    undefined,
    {
      method: 'password',
      inviteToken,
      ua: window.navigator.userAgent,
    },
    recaptchaToken
  );

  clearInvitationToken();

  setCache(AUTH_TOKEN_CACHE_KEY, token);
  // ENG-5251 (PR #2688 review): see logout() comment — keep runtime.token
  // in lockstep with the cache. The window.location.assign reload below
  // would otherwise be the only thing that re-populates runtime.token, and
  // any callers reading it between this point and the reload would see
  // the stale value.
  store.dispatch(setRuntimeVariable('token', token?.token));

  window.location.assign(next);
}

export function useAuth(): { viewer: Viewer; userContext: UserContext } {
  const { viewer, store } = useApp();
  return { viewer, userContext: store.getState().ui.userContext };
}
